import React, { useState } from 'react';
import { theme } from '../../theme';
import { DateInput } from '../DateTimeInputs';
import { TimeInput } from '../DateTimeInputs';

interface EditLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { location: string; mapsUrl: string; date: string; time: string; price: string }) => void;
  currentLocation: string;
  currentMapsUrl: string;
  currentDate: string;
  currentTime: string;
  currentPrice: string;
  loading?: boolean;
}

export const EditLocationModal: React.FC<EditLocationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentLocation,
  currentMapsUrl,
  currentDate,
  currentTime,
  currentPrice,
  loading = false
}) => {
  const [location, setLocation] = useState(currentLocation);
  const [mapsUrl, setMapsUrl] = useState(currentMapsUrl);
  const [date, setDate] = useState(currentDate);
  const [time, setTime] = useState(currentTime);
  const [price, setPrice] = useState(currentPrice);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ location, mapsUrl, date, time, price });
  };

  const handleClose = () => {
    setLocation(currentLocation);
    setMapsUrl(currentMapsUrl);
    setDate(currentDate);
    setTime(currentTime);
    setPrice(currentPrice);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: theme.spacing.md
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.lg,
        maxWidth: '500px',
        width: '100%',
        border: `2px solid ${theme.colors.primary}`,
        boxShadow: theme.colors.shadow
      }}>
        <h3 style={{
          margin: '0 0 24px 0',
          color: theme.colors.primary,
          fontFamily: theme.fonts.heading,
          fontSize: theme.fonts.sizeSubtitle,
          letterSpacing: theme.fonts.letterSpacingWide,
          textTransform: 'uppercase',
          textAlign: 'center'
        }}>
          ✏️ Editar Datos del Partido
        </h3>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
            <label htmlFor="edit-location-name" style={{
              color: theme.colors.text,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              fontSize: theme.fonts.sizeBody
            }}>
              Nombre de la cancha:
            </label>
            <input
              id="edit-location-name"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej: Cancha Sucre"
              required
              disabled={loading}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.input,
                fontSize: theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
            <label htmlFor="edit-location-maps-url" style={{
              color: theme.colors.text,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              fontSize: theme.fonts.sizeBody
            }}>
              Link de Google Maps:
            </label>
            <input
              id="edit-location-maps-url"
              type="url"
              value={mapsUrl}
              onChange={(e) => setMapsUrl(e.target.value)}
              placeholder="Pega aquí el link de Google Maps"
              required
              disabled={loading}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.input,
                fontSize: theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1
              }}
            />
            <small style={{ 
              color: theme.colors.textSecondary, 
              fontSize: theme.fonts.sizeSmall, 
              fontStyle: 'italic',
              fontFamily: theme.fonts.body
            }}>
              💡 Para obtener el link: busca la ubicación en Google Maps, haz clic en "Compartir" y copia el enlace
            </small>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
              <TimeInput
                value={time}
                onChange={(newTime) => setTime(newTime)}
                label="Horario"
                required
              />
              
              <DateInput
                value={date}
                onChange={(newDate) => setDate(newDate)}
                label="Fecha"
                required
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
            <label htmlFor="edit-location-price" style={{
              color: theme.colors.text,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              fontSize: theme.fonts.sizeBody
            }}>
              Precio de la cancha:
            </label>
            <input
              id="edit-location-price"
              type="text"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Ej: 10000"
              required
              disabled={loading}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.input,
                fontSize: theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1
              }}
            />
          </div>

          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            gap: theme.spacing.md, 
            justifyContent: 'center',
            marginTop: theme.spacing.md
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                backgroundColor: theme.colors.danger,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontSize: theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                backgroundColor: theme.colors.primary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontSize: theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 